import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck } from '@fortawesome/pro-duotone-svg-icons/faCheck';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';
import { TooltipDirective } from '../../ui/tooltip/tooltip.directive';
import { faTriangleExclamation } from '@fortawesome/pro-duotone-svg-icons';

@Component({
  selector: 'andor-text-input',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ClipboardModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    TooltipDirective,
  ],
  templateUrl: './text-input.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './text-input.scss',
})
export class TextInput implements OnInit {
  @Input() label = '';
  @Input() mode: 'flat' | 'full' = 'full';
  @Input({ required: true }) control!: FormControl;
  @Input() actionSuccess = false;
  @Input() showAction = false;
  @Input() toolTipMsg = '';
  @Input() errorMsg = '';
  @Input() placeholder = '';
  @Input() type: 'text' | 'password' = 'text';
  @Input() autocomplete: string = 'off';
  @Output() actionClicked = new EventEmitter<string>();
  protected readonly faCheck = faCheck;
  toolTip = signal('');

  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.toolTip.set(this.toolTipMsg);
    // The template reads validity/touched state off the parent-owned control.
    // Typing in this view marks it dirty by itself, but programmatic changes
    // (setValue/reset from the parent, async validators) don't — re-render on
    // every control event so the error icon and action-button state stay fresh.
    this.control.events
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._cdr.markForCheck());
  }

  onActionClicked() {
    if (this.control.valid) {
      this.actionClicked.emit(this.control.value);
    }
  }

  onEnterPressed(event: Event) {
    // Suppress the browser's implicit form submission (page reload) when the
    // input is rendered inside a <form> without an Angular form directive.
    event.preventDefault();
    this.onActionClicked();
  }

  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faTriangleExclamation = faTriangleExclamation;
}
